import { CommissionSightClient } from '@commissionsight/sdk';
import { useCallback, useMemo, useState } from 'react';
import { ErrorBoundary } from './components/ErrorBoundary.js';
import { HelpDrawer } from './components/HelpDrawer.js';
import { Toaster, pushToast } from './components/Toast.js';
import { LogoMark, type Notify, TabIcon } from './components/ui.js';
import { Accounts } from './pages/Accounts.js';
import { AiOps } from './pages/AiOps.js';
import { Carriers } from './pages/Carriers.js';
import { Errors } from './pages/Errors.js';
import { Health } from './pages/Health.js';
import { Jobs } from './pages/Jobs.js';
import { Login } from './pages/Login.js';
import { Logs } from './pages/Logs.js';
import { Marketing } from './pages/Marketing.js';
import { Metrics } from './pages/Metrics.js';
import { PriceCalculator } from './pages/PriceCalculator.js';
import { Referrals } from './pages/Referrals.js';
import { Revenue } from './pages/Revenue.js';
import { System } from './pages/System.js';
import { Users } from './pages/Users.js';
import { BUILD } from './version.js';

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:8787/v1';

// Token storage decision (see also apps/web/src/api.tsx): the admin session token
// lives in localStorage, NOT an httpOnly cookie — same cross-origin Bearer-auth
// rationale as the web app. The admin token is HIGH-VALUE (back-office control),
// so this is the first thing to revisit (httpOnly + SameSite cookie + CSRF, or a
// short token TTL) before the admin app is exposed beyond the internal allowlist.
const TOKEN_KEY = 'cs.admin.token';

export function App() {
  const [token, setToken] = useState<string | null>(() => localStorage.getItem(TOKEN_KEY));
  const client = useMemo(
    () => new CommissionSightClient({ baseUrl: BASE_URL, token: token ?? undefined }),
    [token],
  );

  const signIn = (t: string) => {
    localStorage.setItem(TOKEN_KEY, t);
    setToken(t);
  };
  const signOut = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };

  if (!token) return <Login onSignIn={signIn} />;
  return <Console client={client} onSignOut={signOut} />;
}

type Tab =
  | 'metrics'
  | 'health'
  | 'revenue'
  | 'referrals'
  | 'calculator'
  | 'accounts'
  | 'users'
  | 'carriers'
  | 'ai'
  | 'marketing'
  | 'jobs'
  | 'logs'
  | 'errors'
  | 'system';

const TAB_META: Record<Tab, { label: string; icon: string }> = {
  metrics: { label: 'Metrics', icon: 'M4 19V5M4 19h16M8 15l3-4 3 2 4-6' },
  // Lucide "activity" — the needs-attention board.
  health: { label: 'Health', icon: 'M22 12h-4l-3 9L9 3l-3 9H2' },
  // Lucide "dollar-sign" — revenue.
  revenue: { label: 'Revenue', icon: 'M12 1v22M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6' },
  // Lucide "gift" — the referral program.
  referrals: {
    label: 'Referrals',
    icon: 'M20 12v10H4V12M2 7h20v5H2zM12 22V7M12 7H7.5a2.5 2.5 0 010-5C11 2 12 7 12 7zM12 7h4.5a2.5 2.5 0 000-5C13 2 12 7 12 7z',
  },
  // Lucide "calculator" — internal price-quote tool.
  calculator: {
    label: 'Calculator',
    icon: 'M4 2h16a2 2 0 012 2v16a2 2 0 01-2 2H4a2 2 0 01-2-2V4a2 2 0 012-2zM8 6h8M8 10h.01M12 10h.01M16 10h.01M8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h4',
  },
  accounts: {
    label: 'Accounts',
    icon: 'M3 21v-2a4 4 0 014-4h4a4 4 0 014 4v2M9 11a4 4 0 100-8 4 4 0 000 8z',
  },
  // Lucide "users" — a clear two-people glyph that fills the viewBox.
  users: {
    label: 'Users',
    icon: 'M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2M13 7a4 4 0 11-8 0 4 4 0 018 0M22 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75',
  },
  carriers: { label: 'Carriers', icon: 'M3 7l9-4 9 4-9 4-9-4zM3 7v10l9 4 9-4V7' },
  // Lucide "sparkles" — the AI agent ops view.
  ai: {
    label: 'AI',
    icon: 'M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9L12 3zM19 3v4M21 5h-4',
  },
  // Lucide "send" — cold outreach.
  marketing: { label: 'Marketing', icon: 'M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z' },
  jobs: { label: 'Jobs', icon: 'M4 6h16M4 12h16M4 18h10' },
  logs: {
    label: 'Logs',
    icon: 'M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8zM14 2v6h6M16 13H8M16 17H8M10 9H8',
  },
  // Lucide "alert-triangle" — the error/issue radar.
  errors: {
    label: 'Errors',
    icon: 'M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0zM12 9v4M12 17h.01',
  },
  // Lucide "timer" — scheduled / cron maintenance.
  system: {
    label: 'System',
    icon: 'M10 2h4M12 14l3-3M12 22a8 8 0 100-16 8 8 0 000 16z',
  },
};

/** Map each tab to the most relevant Help section so the drawer deep-links there. */
const HELP_FOR: Record<Tab, string> = {
  metrics: 'metrics',
  health: 'metrics',
  revenue: 'revenue',
  referrals: 'revenue',
  calculator: 'revenue',
  accounts: 'accounts',
  users: 'users',
  carriers: 'carriers',
  ai: 'carriers',
  marketing: 'marketing',
  jobs: 'jobs',
  logs: 'logs',
  errors: 'logs',
  system: 'system',
};

function Console({ client, onSignOut }: { client: CommissionSightClient; onSignOut: () => void }) {
  const [tab, setTab] = useState<Tab>('metrics');
  const [helpOpen, setHelpOpen] = useState(false);
  const notify = useCallback<Notify>((msg, tone = 'error') => pushToast(msg, { tone }), []);
  const tabs: Tab[] = [
    'metrics',
    'health',
    'revenue',
    'referrals',
    'calculator',
    'accounts',
    'users',
    'carriers',
    'ai',
    'marketing',
    'jobs',
    'logs',
    'errors',
    'system',
  ];

  return (
    <div className="min-h-screen bg-neutral-50">
      <div className="mx-auto flex min-h-screen w-full gap-6 px-6">
        {/* Left sidebar navigation — vertical so it never overflows as modules grow. */}
        <aside className="sticky top-0 flex h-screen w-56 shrink-0 flex-col border-r border-neutral-200/70 py-4 pr-3">
          <h1 className="brand flex min-w-0 items-center gap-2.5 px-2 text-lg">
            <LogoMark size={24} />
            <span className="truncate">
              Commission<span className="text-emerald-600">Sight</span>
            </span>
            <span className="shrink-0 rounded-full bg-neutral-900 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
              Admin
            </span>
          </h1>
          <nav className="mt-5 flex flex-1 flex-col gap-0.5 overflow-y-auto pr-1">
            {tabs.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setTab(t)}
                className={`inline-flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium transition ${
                  tab === t
                    ? 'bg-emerald-600 text-white shadow-sm'
                    : 'text-neutral-500 hover:bg-neutral-100 hover:text-neutral-900'
                }`}
              >
                <TabIcon path={TAB_META[t].icon} />
                {TAB_META[t].label}
              </button>
            ))}
          </nav>
          <div className="mt-3 flex flex-col gap-0.5 border-t border-neutral-200/70 pt-3">
            <button
              type="button"
              className="inline-flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-neutral-500 transition hover:bg-neutral-100 hover:text-neutral-900"
              onClick={() => setHelpOpen(true)}
            >
              <TabIcon path="M12 17h.01M12 14a2 2 0 00.5-3.9 2 2 0 10-2.5 1.9M12 22a10 10 0 100-20 10 10 0 000 20z" />
              Help
            </button>
            <button
              type="button"
              className="inline-flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-neutral-500 transition hover:bg-neutral-100 hover:text-neutral-900"
              onClick={onSignOut}
            >
              <TabIcon path="M15 17l5-5-5-5M20 12H9M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
              Sign out
            </button>
          </div>
        </aside>

        {/* Main content — fills the rest of the 90%-wide shell. */}
        <div className="flex min-w-0 flex-1 flex-col">
          <main className="flex-1 py-6">
            <ErrorBoundary key={tab}>
              {tab === 'metrics' && <Metrics client={client} />}
              {tab === 'health' && <Health />}
              {tab === 'revenue' && <Revenue client={client} />}
              {tab === 'referrals' && <Referrals />}
              {tab === 'calculator' && <PriceCalculator notify={notify} />}
              {tab === 'accounts' && <Accounts client={client} notify={notify} />}
              {tab === 'users' && <Users client={client} notify={notify} />}
              {tab === 'carriers' && <Carriers client={client} notify={notify} />}
              {tab === 'ai' && <AiOps notify={notify} />}
              {tab === 'marketing' && <Marketing notify={notify} />}
              {tab === 'jobs' && <Jobs client={client} notify={notify} />}
              {tab === 'logs' && <Logs />}
              {tab === 'errors' && <Errors notify={notify} />}
              {tab === 'system' && <System client={client} />}
            </ErrorBoundary>
          </main>
          <footer className="border-t border-neutral-200/70 py-3 text-center text-xs text-neutral-400">
            CommissionSight · admin <span className="font-mono">{BUILD.version}</span> · built{' '}
            {BUILD.date}
          </footer>
        </div>
      </div>
      <Toaster />
      <HelpDrawer open={helpOpen} onClose={() => setHelpOpen(false)} section={HELP_FOR[tab]} />
    </div>
  );
}
